import { Timestamp } from '@bufbuild/protobuf/wkt';
import { TFunction } from 'i18next';
import { JsonValue, toJson } from '@bufbuild/protobuf';
import {
  Device,
  Device_ReleaseMode,
  Device_Status,
  DeviceSchema,
} from '@wavingroup/iot-core/gen/ts/wavin/blaze/v1/device_pb';
import { DeviceConfig } from '@wavingroup/iot-core/gen/ts/wavin/sentio/v1/device_config_pb';
import { normalizeString } from '~/shared/utils/normalizeString';
import { BrokerValuesModel } from '~/shared/models/device/BrokerValuesModel';
import { DeviceFirmwareModel } from '~/shared/models/device/DeviceFirmwareModel';

export class DeviceModel {
  readonly brokerValues?: BrokerValuesModel;

  readonly firmwareModel: DeviceFirmwareModel;

  readonly deviceRoomsList?: { roomId: string; roomName: string }[];

  readonly isInManualReleaseMode: boolean;

  readonly isOnline: boolean;

  readonly lastConfig?: DeviceConfig;

  readonly lastHeartbeat?: Timestamp;

  readonly name: string;

  readonly rawDevice: JsonValue;

  readonly registrationKey: string;

  readonly releaseMode: Device_ReleaseMode;

  readonly searchKeywords: string;

  readonly serialNumber: string;

  readonly title: string;

  constructor(device: Device, fallbackBrokerValues?: BrokerValuesModel) {
    const sentioConfig =
      device.lastConfig?.data.case === 'sentio'
        ? device.lastConfig.data.value
        : undefined;
    const sentioSource =
      device.lastConfig?.source.case === 'sentioSource'
        ? device.lastConfig.source.value
        : undefined;

    this.rawDevice = toJson(DeviceSchema, device);
    this.name = device.name;
    this.serialNumber = device.serialNumber;
    this.registrationKey = device.registrationKey;
    this.title = sentioConfig?.titlePersonalized || sentioConfig?.title || '';

    this.firmwareModel = new DeviceFirmwareModel(
      device.firmwareAvailable,
      device.firmwareInstalled,
      device.firmwareUpdateStatus,
      device.releaseMode === Device_ReleaseMode.AUTOMATIC,
    );

    this.deviceRoomsList = sentioConfig?.rooms.map((room) => ({
      roomId: room.id,
      roomName: room.title ?? '',
    }));

    if (fallbackBrokerValues) {
      this.brokerValues = fallbackBrokerValues;
    }
    const objectTree = sentioSource?.objectTree;
    if (objectTree) {
      this.brokerValues = new BrokerValuesModel(objectTree);
    }

    this.lastHeartbeat = device.lastHeartbeat;

    this.searchKeywords = [
      this.title,
      device.serialNumber,
      device.registrationKey,
    ]
      .filter(Boolean)
      .map(normalizeString)
      .join(' | ');

    this.isOnline = device.status === Device_Status.OK;

    this.releaseMode = device.releaseMode;

    this.isInManualReleaseMode =
      device.releaseMode === Device_ReleaseMode.MANUAL;
  }

  getReleaseModeChangeDialogTexts = (t: TFunction) => {
    const { releaseMode } = this;

    return {
      title: t('devicePage.firmware.releaseMode.dialogTitle'),
      description:
        releaseMode === Device_ReleaseMode.AUTOMATIC
          ? t('devicePage.firmware.releaseMode.changeToManualMessage')
          : t('devicePage.firmware.releaseMode.changeToAutomaticMessage'),
    };
  };

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  getReleaseModeOptions = (t: TFunction) => [
    {
      label: t('devicePage.firmware.releaseMode.automatic'),
      value: Device_ReleaseMode.AUTOMATIC,
    },
    {
      label: t('devicePage.firmware.releaseMode.manual'),
      value: Device_ReleaseMode.MANUAL,
    },
  ];
}
